import { motion } from 'motion/react';
import { ArrowLeft, Search, BookOpen, MessageCircle, Users, Bell, Globe } from 'lucide-react';
import logoImage from '../assets/f736bb54a1ccb5a9a30d8b8ec9d3974d4b7ef9d6.png';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';
import goldIcon from '../assets/gold icon.png';
// Imported logo images as strings
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { useState } from 'react';
import { ParallaxBackground } from './utils/parallax';
import { useTheme } from './ThemeProvider';
import { useNotificationBadges } from './NotificationContext';
import { useUserData } from './UserDataContext';
import { UnreadBadge } from './ui/UnreadBadge';

interface AllHistoryScreenProps {
  onBack: () => void;
}

// Geographic regions — align with iOS region taxonomy
const REGIONS = ['All', 'West Africa', 'East Africa', 'North Africa', 'Southern Africa', 'Central Africa', 'Diaspora'] as const;
type Region = typeof REGIONS[number];

const allHistoryTopics = [
  // Ancient Civilizations
  {
    id: 'ancient-egypt',
    title: 'Ancient Egypt',
    description: 'Discover the pharaohs, pyramids, and advanced civilization that shaped history',
    image: 'https://images.unsplash.com/photo-1606419598102-95ead4deaf4f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Ancient Civilizations',
    region: 'North Africa' as Region,
    discussions: '1.2K',
    members: '8.4K',
  },
  {
    id: 'kingdom-of-kush',
    title: 'Kingdom of Kush',
    description: 'Learn about the powerful Nubian kingdom and its golden age',
    image: 'https://images.unsplash.com/photo-1667765460178-db9eae077f00?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Ancient Civilizations',
    region: 'East Africa' as Region,
    discussions: '687',
    members: '4.2K',
  },
  {
    id: 'great-zimbabwe',
    title: 'Great Zimbabwe',
    description: 'Explore the ancient stone city and powerful trading empire',
    image: 'https://images.unsplash.com/photo-1638467347083-b67984609977?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Ancient Civilizations',
    region: 'Southern Africa' as Region,
    discussions: '523',
    members: '3.8K',
  },
  {
    id: 'carthage-empire',
    title: 'Carthage Empire',
    description: 'The North African power that challenged Rome',
    image: 'https://images.unsplash.com/photo-1591825144138-bb0dc10f26b8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Ancient Civilizations',
    region: 'North Africa' as Region,
    discussions: '892',
    members: '5.6K',
  },
  {
    id: 'aksumite-empire',
    title: 'Aksumite Empire',
    description: 'The powerful trading civilization of ancient Ethiopia',
    image: 'https://images.unsplash.com/photo-1609137144813-7d9921338f24?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Ancient Civilizations',
    region: 'East Africa' as Region,
    discussions: '445',
    members: '3.2K',
  },

  // Medieval Kingdoms
  {
    id: 'mali-empire',
    title: 'Mali Empire',
    description: 'Discover the wealth and scholarship of Timbuktu and Mansa Musa',
    image: 'https://images.unsplash.com/photo-1661704813500-5d3cc7a0ce89?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Medieval Kingdoms',
    region: 'West Africa' as Region,
    discussions: '1.5K',
    members: '9.8K',
  },
  {
    id: 'benin-kingdom',
    title: 'Benin Kingdom',
    description: 'Explore the bronze artistry and royal heritage of the Benin Empire',
    image: 'https://images.unsplash.com/photo-1696224742102-26309ea4d181?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Medieval Kingdoms',
    region: 'West Africa' as Region,
    discussions: '978',
    members: '6.4K',
  },
  {
    id: 'songhai-empire',
    title: 'Songhai Empire',
    description: 'The largest empire in African history and its scholars',
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Medieval Kingdoms',
    region: 'West Africa' as Region,
    discussions: '734',
    members: '5.1K',
  },
  {
    id: 'kingdom-of-kongo',
    title: 'Kingdom of Kongo',
    description: 'Central African kingdom and early European contact',
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Medieval Kingdoms',
    region: 'Central Africa' as Region,
    discussions: '612',
    members: '4.5K',
  },
  {
    id: 'ashanti-empire',
    title: 'Ashanti Empire',
    description: 'The Golden Stool and powerful West African empire',
    image: 'https://images.unsplash.com/photo-1609137144813-7d9921338f24?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Medieval Kingdoms',
    region: 'West Africa' as Region,
    discussions: '823',
    members: '5.9K',
  },

  // Colonial Era
  {
    id: 'scramble-for-africa',
    title: 'Scramble for Africa',
    description: 'European colonization and the Berlin Conference of 1884',
    image: 'https://images.unsplash.com/photo-1604395516356-0e4e3ed36f9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Colonial Era',
    region: 'West Africa' as Region,
    discussions: '2.1K',
    members: '12.3K',
  },
  {
    id: 'zulu-kingdom-wars',
    title: 'Zulu Kingdom & Wars',
    description: 'Shaka Zulu and the resistance against colonialism',
    image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Colonial Era',
    region: 'Southern Africa' as Region,
    discussions: '1.4K',
    members: '8.7K',
  },
  {
    id: 'haitian-revolution',
    title: 'Haitian Revolution',
    description: 'The successful slave revolt that created the first Black republic',
    image: 'https://images.unsplash.com/photo-1575481672230-7a2c97c87535?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Colonial Era',
    region: 'Diaspora' as Region,
    discussions: '1.8K',
    members: '10.5K',
  },
  {
    id: 'maji-maji-rebellion',
    title: 'Maji Maji Rebellion',
    description: 'East African resistance against German colonial rule',
    image: 'https://images.unsplash.com/photo-1544359137-46d9e86522c5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Colonial Era',
    region: 'East Africa' as Region,
    discussions: '567',
    members: '3.9K',
  },

  // Independence & Liberation
  {
    id: 'pan-africanism-movement',
    title: 'Pan-Africanism Movement',
    description: 'Unity movements and the fight for independence',
    image: 'https://images.unsplash.com/photo-1589300271695-4bb3d06a6b5a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Independence & Liberation',
    discussions: '2.5K',
    members: '14.2K',
  },
  {
    id: 'ghanaian-independence',
    title: 'Ghanaian Independence',
    description: 'Kwame Nkrumah and the first sub-Saharan nation to gain independence',
    image: 'https://images.unsplash.com/photo-1604395516356-0e4e3ed36f9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Independence & Liberation',
    discussions: '1.3K',
    members: '7.8K',
  },
  {
    id: 'apartheid-liberation',
    title: 'Apartheid & Liberation',
    description: 'The struggle against apartheid and Nelson Mandela\'s legacy',
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Independence & Liberation',
    discussions: '3.2K',
    members: '18.6K',
  },
  {
    id: 'mau-mau-uprising',
    title: 'Mau Mau Uprising',
    description: 'Kenyan independence movement and colonial resistance',
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Independence & Liberation',
    discussions: '892',
    members: '5.4K',
  },

  // Pre-Colonial Culture
  {
    id: 'african-philosophy',
    title: 'African Philosophy',
    description: 'Traditional wisdom, Ubuntu, and philosophical traditions',
    image: 'https://images.unsplash.com/photo-1609137144813-7d9921338f24?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Culture & Heritage',
    discussions: '1.6K',
    members: '9.2K',
  },
  {
    id: 'traditional-religions',
    title: 'Traditional Religions',
    description: 'Indigenous belief systems and spiritual practices',
    image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Culture & Heritage',
    discussions: '1.1K',
    members: '6.8K',
  },
  {
    id: 'ancient-african-mathematics',
    title: 'Ancient African Mathematics',
    description: 'Mathematical innovations from ancient African civilizations',
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Culture & Heritage',
    discussions: '734',
    members: '4.7K',
  },
  {
    id: 'african-architecture',
    title: 'African Architecture',
    description: 'Traditional building techniques and architectural marvels',
    image: 'https://images.unsplash.com/photo-1638467347083-b67984609977?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Culture & Heritage',
    discussions: '945',
    members: '6.1K',
  },

  // World History
  {
    id: 'transatlantic-slave-trade',
    title: 'Transatlantic Slave Trade',
    description: 'Understanding the history and impact of the slave trade',
    image: 'https://images.unsplash.com/photo-1604395516356-0e4e3ed36f9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'World History',
    discussions: '2.8K',
    members: '15.4K',
  },
  {
    id: 'civil-rights-movement',
    title: 'Civil Rights Movement',
    description: 'Black American struggle for equality and justice',
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'World History',
    discussions: '3.4K',
    members: '19.2K',
  },
  {
    id: 'maroon-communities',
    title: 'Maroon Communities',
    description: 'Escaped slave communities in the Americas',
    image: 'https://images.unsplash.com/photo-1575481672230-7a2c97c87535?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'World History',
    discussions: '678',
    members: '4.3K',
  },
  {
    id: 'harlem-renaissance',
    title: 'Harlem Renaissance',
    description: 'Black American cultural and artistic explosion',
    image: 'https://images.unsplash.com/photo-1589300271695-4bb3d06a6b5a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'World History',
    discussions: '1.5K',
    members: '8.9K',
  },

  // Modern History
  {
    id: 'african-union-formation',
    title: 'African Union Formation',
    description: 'Continental unity and cooperation in the 21st century',
    image: 'https://images.unsplash.com/photo-1604395516356-0e4e3ed36f9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Modern Africa',
    discussions: '1.2K',
    members: '7.6K',
  },
  {
    id: 'african-tech-revolution',
    title: 'African Tech Revolution',
    description: 'Innovation hubs and digital transformation',
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Modern Africa',
    discussions: '2.1K',
    members: '12.8K',
  },
  {
    id: 'contemporary-african-leaders',
    title: 'Contemporary African Leaders',
    description: 'Modern political figures shaping the continent',
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
    era: 'Modern Africa',
    discussions: '1.7K',
    members: '10.3K',
  },
];

export function AllHistoryScreen({ onBack }: AllHistoryScreenProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEra, setSelectedEra] = useState<string>('All');
  const [selectedRegion, setSelectedRegion] = useState<Region>('All');
  const { theme } = useTheme();
  const { unreadCount } = useNotificationBadges();
  const { userProfile } = useUserData();

  const eras = ['All', ...Array.from(new Set(allHistoryTopics.map(t => t.era)))];

  const filteredTopics = allHistoryTopics.filter((topic) => {
    const matchesSearch = topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.era.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesEra    = selectedEra    === 'All' || topic.era    === selectedEra;
    const matchesRegion = selectedRegion === 'All' || (topic as any).region === selectedRegion;

    return matchesSearch && matchesEra && matchesRegion;
  });

  const handleTopicClick = (topic: any) => {
    window.dispatchEvent(new CustomEvent('navigateToHistoryForum', { detail: topic }));
  };

  return (
    // No pb-20 on the root, unlike the screens that sit behind the bottom
    // nav: /explore/history is not one of BOTTOM_NAV_PATHS, so that padding reserved
    // 80px of the viewport for a bar that never renders and the scrolling
    // list below was clipped 80px short of the bottom.
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div className="p-4 border-b border-black/10 dark:border-white/10 bg-white/80 dark:bg-black/30 backdrop-blur-lg"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}>
          <div className="flex items-center justify-center mb-4 relative min-h-[40px]">
            {/* Left: Back, Logo, Notifications */}
            <div className="absolute left-0 flex items-center gap-2">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-black dark:text-white" />
              </motion.button>
              <img src={theme === 'dark' ? String(logoImageWhite) : String(logoImage)} alt="AfroYap" className="h-6 w-auto" />
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'notifications' }))}
                className="relative p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <Bell className="w-5 h-5 text-black dark:text-white" />
                {unreadCount > 0 && (
                  <UnreadBadge count={unreadCount} style={{ top: -8, left: -8 }} />
                )}
              </motion.button>
            </div>

            {/* Center: Title */}
            <h1 className="text-xl font-bold text-black dark:text-white">History</h1>

            {/* Right: Gold Balance */}
            <div className="absolute right-0">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'store' }))}
                className="flex items-center gap-1.5 bg-black/5 dark:bg-white/5 px-2.5 py-1.5 rounded-full border border-black/10 dark:border-white/10 backdrop-blur-sm"
              >
                <img src={goldIcon as any} alt="Gold" className="h-4 w-auto object-contain" />
                <span className="text-black dark:text-white text-xs font-bold">{Number(userProfile.goldBalance || 0).toLocaleString()}</span>
              </motion.button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666] dark:text-[#E5E5E5]" />
            <Input
              placeholder="Search history topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-[#F5F5F5] dark:bg-[#2a2a2a] border-none text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] rounded-xl shadow-[2px_2px_0px_0px_rgba(0,0,0,0.25)] dark:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.1)]"
            />
          </div>

          {/* Era Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {eras.map((era) => (
              <button
                key={era}
                onClick={() => setSelectedEra(era)}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors tab-shadow ${selectedEra === era
                  ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white'
                  : 'bg-[#F5F5F5] dark:bg-[#2a2a2a] text-black dark:text-white hover:bg-[#F39C12]/20 dark:hover:bg-[#4478ea]/20'
                  }`}
              >
                {era}
              </button>
            ))}
          </div>

          {/* Region Filter */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide mt-1">
            <span className="flex items-center gap-1 text-xs text-[#666666] dark:text-[#a0a0a0] shrink-0 pl-0.5">
              <Globe className="w-3.5 h-3.5" /> Region:
            </span>
            {REGIONS.map((region) => (
              <button
                key={region}
                onClick={() => setSelectedRegion(region)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors tab-shadow ${selectedRegion === region
                  ? 'bg-linear-to-r from-[#F39C12]/80 to-[#E74C3C]/80 dark:from-[#4478ea]/80 dark:to-[#2d1ef1]/80 text-white font-semibold'
                  : 'bg-black/5 dark:bg-white/5 text-black dark:text-white hover:bg-[#F39C12]/10 dark:hover:bg-[#4478ea]/10'
                  }`}
              >
                {region}
              </button>
            ))}
          </div>
        </div>

        <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
          <div className="p-4 space-y-4" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}>
            {filteredTopics.map((topic, index) => (
              <motion.div
                key={topic.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleTopicClick(topic)}
                className="relative h-56 rounded-2xl overflow-hidden cursor-pointer group card-shadow"
              >
                <img
                  src={topic.image}
                  alt={topic.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black via-black/70 to-transparent" />
                <div className="absolute top-4 right-4">
                  <span
                    className="px-3 py-1 rounded-full backdrop-blur-sm text-white text-xs border border-white/20"
                    style={{ backgroundColor: theme === 'dark' ? 'rgba(68, 120, 234, 0.9)' : 'rgba(243, 156, 18, 0.9)' }}
                  >
                    {topic.era}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-xl text-white mb-2" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.9)' }}>
                    {topic.title}
                  </h3>
                  <p className="text-sm text-[#E5E5E5] mb-3 line-clamp-2" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.9)' }}>
                    {topic.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-white/90">
                    <div className="flex items-center gap-1">
                      <MessageCircle className="w-3.5 h-3.5" />
                      <span>{topic.discussions} discussions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      <span>{topic.members} learning</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
